class QuickViewProduct extends HTMLElement {
  constructor() {
    super();
    this.button = null;
    this.productUrl = null;
    this.overlay = null;
  }

  connectedCallback() {
    this.button = this.querySelector('button[data-href]');
    if (this.button) {
      this.productUrl = this.button.getAttribute('data-href');
      
      // Remove the inline onclick handler
      this.button.removeAttribute('onclick');
      
      // Use capture phase to intercept clicks before other handlers
      this.button.addEventListener('click', this.handleClick.bind(this), true);
      
      console.log('QuickViewProduct initialized for:', this.productUrl);
    }
  }

  handleClick(e) {
    console.log('Quick view clicked');
    
    // Prevent all default actions
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    // Find the product item container
    const productItem = this.closest('.cart-product-item');
    if (!productItem) {
      console.error('Product item container not found');
      return;
    }

    // Check if overlay already exists
    if (this.overlay) {
      this.removeOverlay();
      return;
    }

    // Fetch product data
    console.log('Fetching product data...');
    this.fetchProductData(productItem);
    
    return false;
  }

  async fetchProductData(productItem) {
    try {
      const response = await fetch(`${this.productUrl}?view=json`);
      
      // If the JSON view doesn't exist, try the .js endpoint
      let productData;
      if (response.ok) {
        productData = await response.json();
      } else {
        // Fallback: fetch product.js
        const jsResponse = await fetch(`${this.productUrl}.js`);
        productData = await jsResponse.json();
      }

      this.showVariantSelector(productItem, productData);
    } catch (error) {
      console.error('Error fetching product data:', error);
      // Fallback: just navigate to the product page
      window.location.href = this.productUrl;
    }
  }

  showVariantSelector(productItem, product) {
    // Create overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'quick-view-overlay';
    
    // Create variant selector
    const variantSelector = document.createElement('div');
    variantSelector.className = 'quick-view-variants';
    
    // Add title
    const title = document.createElement('div');
    title.className = 'quick-view-title';
    title.textContent = 'Select ' + (product.options?.[0] || 'Option');
    variantSelector.appendChild(title);
    
    // Create variant buttons
    const variantsContainer = document.createElement('div');
    variantsContainer.className = 'quick-view-variants-container';
    
    if (product.variants && product.variants.length > 0) {
      product.variants.forEach(variant => {
        if (variant.available) {
          const button = document.createElement('button');
          button.className = 'quick-view-variant-button';
          button.textContent = variant.option1 || variant.title;
          button.dataset.variantId = variant.id;
          
          button.addEventListener('click', (e) => {
            e.stopPropagation();
            this.addToCart(variant.id, productItem);
          });
          
          variantsContainer.appendChild(button);
        }
      });
    }
    
    variantSelector.appendChild(variantsContainer);
    
    // Add close button
    const closeButton = document.createElement('button');
    closeButton.className = 'quick-view-close';
    closeButton.innerHTML = '&times;';
    closeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.removeOverlay();
    });
    variantSelector.appendChild(closeButton);
    
    // Append to overlay
    this.overlay.appendChild(variantSelector);
    
    // Click outside to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.removeOverlay();
      }
    });
    
    // Add to product item
    productItem.style.position = 'relative';
    productItem.appendChild(this.overlay);
    
    // Trigger animation
    requestAnimationFrame(() => {
      this.overlay.classList.add('active');
    });
  }

  async addToCart(variantId, productItem) {
    try {
      // Add to cart
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Remove overlay
        this.removeOverlay();
        
        // Trigger cart update (if using AJAX cart)
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );
        
        // Optional: Show success feedback
        this.showSuccessFeedback(productItem);
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Sorry, there was an error adding this item to your cart.');
    }
  }

  showSuccessFeedback(productItem) {
    const feedback = document.createElement('div');
    feedback.className = 'quick-view-success';
    feedback.textContent = '✓ Added to cart';
    productItem.appendChild(feedback);
    
    setTimeout(() => {
      feedback.classList.add('fade-out');
      setTimeout(() => feedback.remove(), 300);
    }, 2000);
  }

  removeOverlay() {
    if (this.overlay) {
      this.overlay.classList.remove('active');
      setTimeout(() => {
        if (this.overlay && this.overlay.parentNode) {
          this.overlay.parentNode.removeChild(this.overlay);
        }
        this.overlay = null;
      }, 300);
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick, true);
    }
    this.removeOverlay();
  }
}

// Register the custom element
if (!customElements.get('quick-view-product')) {
  customElements.define('quick-view-product', QuickViewProduct);
  console.log('QuickViewProduct custom element registered');
}